import React from 'react';
import ColorEditor from '../ColorEditor';

const IframeEditor = ({ content, onContentChange, businessInfo, site, onCreateCustomTheme }) => {

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Source URL *
        </label>
        <input
          type="url"
          value={content.src || ''}
          onChange={(e) => onContentChange('src', e.target.value)}
          placeholder="https://example.com"
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            content.src && !isValidUrl(content.src) ? 'border-red-500' : 'border-gray-300'
          }`}
          required
        />
        {content.src && !isValidUrl(content.src) && (
          <p className="mt-1 text-sm text-red-600">Please enter a valid URL</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title (for accessibility)
        </label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => onContentChange('title', e.target.value)}
          placeholder="Embedded content description"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Width
          </label>
          <input
            type="text"
            value={content.width || '100%'}
            onChange={(e) => onContentChange('width', e.target.value)}
            placeholder="100%, 600px, auto"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Height
          </label>
          <input
            type="text"
            value={content.height || '400px'}
            onChange={(e) => onContentChange('height', e.target.value)}
            placeholder="400px, 50vh"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="allowFullscreen"
            checked={content.allowFullscreen || false}
            onChange={(e) => onContentChange('allowFullscreen', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="allowFullscreen" className="ml-2 text-sm text-gray-700">
            Allow fullscreen
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="responsive"
            checked={content.responsive !== false}
            onChange={(e) => onContentChange('responsive', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="responsive" className="ml-2 text-sm text-gray-700">
            Responsive (adjusts to container width)
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Loading Strategy
        </label>
        <select
          value={content.loading || 'lazy'}
          onChange={(e) => onContentChange('loading', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="lazy">Lazy (load when visible)</option>
          <option value="eager">Eager (load immediately)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Border Style
        </label>
        <select
          value={content.border || 'none'}
          onChange={(e) => onContentChange('border', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="none">No border</option>
          <option value="1px solid #e5e7eb">Light border</option>
          <option value="2px solid #d1d5db">Medium border</option>
          <option value="1px solid #9ca3af">Dark border</option>
        </select>
      </div>

      {content.src && !isValidUrl(content.src) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-sm text-yellow-800">
            ⚠️ Please enter a valid URL before saving the section.
          </p>
        </div>
      )}

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default IframeEditor;